import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';

const SOIL_TYPES = ['Killi','Kumlu','Tınlı','Humuslu','Kireçli','Karma'];
const EMPTY = { name: '', sizeDecare: '', soilType: '', notes: '', currentCrop: '' };

export default function FieldsPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/farm/fields').then(r => setItems(r.data.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/farm/fields/${editingId}`, form);
        toast.success(t('settings.saved'));
      } else {
        await api.post('/farm/fields', form);
        toast.success(t('settings.saved'));
      }
      setShowModal(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch { toast.error(t('settings.saveFailed')); }
    finally { setSaving(false); }
  };

  const startEdit = (item) => {
    setForm({
      name: item.name || '',
      sizeDecare: item.size_decare || '',
      soilType: item.soil_type || '',
      notes: item.notes || '',
      currentCrop: item.current_crop || '',
    });
    setEditingId(item.id);
    setShowModal(true);
  };

  const totalDecare = items.reduce((s, i) => s + parseFloat(i.size_decare || 0), 0);

  return (
    <div>
      <div className="card-row" style={{ marginBottom: 16 }}>
        <h2 className="section-title" style={{ margin: 0 }}>{t('nav.fields')}</h2>
        {totalDecare > 0 && (
          <span style={{ fontWeight: 800, color: '#2d5a27' }}>
            {new Intl.NumberFormat('tr-TR').format(totalDecare)} da
          </span>
        )}
      </div>

      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : items.length === 0 ? (
            <div className="empty-state"><div className="icon">🗺️</div><p>{t('common.noData')}</p></div>
          ) : items.map(item => (
            <div key={item.id} className="txn-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
                <div className="txn-main">
                  <div className="txn-name">🌿 {item.name}</div>
                  <div className="txn-meta">
                    {item.soil_type && `${item.soil_type} toprak`}
                    {item.current_crop && ` · 🌾 ${item.current_crop}`}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {item.size_decare && (
                    <span style={{ fontWeight: 800, color: '#2d5a27' }}>{item.size_decare} da</span>
                  )}
                </div>
              </div>
              {expandedId === item.id && (
                <div style={{ marginTop: 8, padding: 8, background: '#f9f9f9', borderRadius: 8, fontSize: '0.82rem' }}>
                  {item.notes && <div style={{ marginBottom: 6, color: '#666' }}>{item.notes}</div>}
                  <button onClick={() => startEdit(item)}
                    style={{ background: 'var(--green-mid)', color: '#fff', border: 'none', borderRadius: 8,
                      padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                    {t('common.edit')}
                  </button>
                </div>
              )}
            </div>
          ))}
      </div>

      <button className="btn-fab" onClick={() => { setForm(EMPTY); setEditingId(null); setShowModal(true); }}>+</button>

      {showModal && (
        <Modal title={editingId ? 'Tarla Düzenle' : 'Yeni Tarla Ekle 🌿'} onClose={() => { setShowModal(false); setEditingId(null); }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('workOrders.field')} *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="Örn: Kuzey Tarlası, Bahçe..." required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Büyüklük (dekar)</label>
                <input type="number" step="0.1" value={form.sizeDecare}
                  onChange={e => set('sizeDecare', e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label>Toprak Türü</label>
                <select value={form.soilType} onChange={e => set('soilType', e.target.value)}>
                  <option value="">Seçin</option>
                  {SOIL_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Ekili Ürün</label>
              <input value={form.currentCrop} onChange={e => set('currentCrop', e.target.value)}
                placeholder="Buğday, Domates..." />
            </div>
            <div className="form-group">
              <label>{t('payments.notes')}</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
                placeholder="Sulama durumu, özellikler..." />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
                onClick={() => { setShowModal(false); setEditingId(null); }}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
                {saving ? t('common.loading') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
